import { createTheme, alpha } from '@mui/material/styles';

// Modern color palette
const colors = {
  // Primary colors - Modern blue gradient
  primary: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6',
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
  },
  // Secondary colors - Modern purple
  secondary: {
    50: '#faf5ff',
    100: '#f3e8ff',
    200: '#e9d5ff',
    300: '#d8b4fe',
    400: '#c084fc',
    500: '#a855f7',
    600: '#9333ea',
    700: '#7c3aed',
    800: '#6b21a8',
    900: '#581c87',
  },
  // Neutral colors
  grey: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
  },
  // Success colors
  success: {
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',
    500: '#22c55e',
    600: '#16a34a',
    700: '#15803d',
    800: '#166534',
    900: '#14532d',
  },
  // Warning colors
  warning: {
    50: '#fffbeb',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    400: '#fbbf24',
    500: '#f59e0b',
    600: '#d97706',
    700: '#b45309',
    800: '#92400e',
    900: '#78350f',
  },
  // Error colors
  error: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
  },
  // Info colors
  info: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6',
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
  },
};

// Create the theme
export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: colors.primary[600],
      light: colors.primary[400],
      dark: colors.primary[800],
      contrastText: '#ffffff',
    },
    secondary: {
      main: colors.secondary[600],
      light: colors.secondary[400],
      dark: colors.secondary[800],
      contrastText: '#ffffff',
    },
    error: {
      main: colors.error[500],
      light: colors.error[400],
      dark: colors.error[700],
    },
    warning: {
      main: colors.warning[500],
      light: colors.warning[400],
      dark: colors.warning[700],
    },
    info: {
      main: colors.info[500],
      light: colors.info[400],
      dark: colors.info[700],
    },
    success: {
      main: colors.success[500],
      light: colors.success[400],
      dark: colors.success[700],
    },
    grey: colors.grey,
    background: {
      default: colors.grey[50],
      paper: '#ffffff',
    },
    text: {
      primary: colors.grey[900],
      secondary: colors.grey[600],
    },
  },
  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,
    h1: {
      fontSize: '1.875rem', // 30px
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '1.5rem', // 24px
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: '1.25rem', // 20px
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontSize: '1.125rem', // 18px
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1rem', // 16px
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h6: {
      fontSize: '0.875rem', // 14px
      fontWeight: 600,
      lineHeight: 1.4,
    },
    subtitle1: {
      fontSize: '0.875rem', // 14px
      fontWeight: 500,
      lineHeight: 1.4,
    },
    subtitle2: {
      fontSize: '0.75rem', // 12px
      fontWeight: 500,
      lineHeight: 1.4,
    },
    body1: {
      fontSize: '0.875rem', // 14px
      fontWeight: 400,
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.75rem', // 12px
      fontWeight: 400,
      lineHeight: 1.5,
    },
    button: {
      fontSize: '0.75rem', // 12px
      fontWeight: 600,
      lineHeight: 1.4,
      textTransform: 'none' as const,
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 600,
      lineHeight: 1.5,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.1em',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          boxSizing: 'border-box',
        },
        html: {
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          height: '100%',
          width: '100%',
        },
        body: {
          height: '100%',
          width: '100%',
          backgroundColor: colors.grey[50],
        },
        '#root': {
          height: '100%',
          width: '100%',
        },
        'input:-webkit-autofill': {
          WebkitBoxShadow: `0 0 0 1000px ${colors.grey[50]} inset`,
          WebkitTextFillColor: colors.grey[900],
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: colors.grey[900],
          boxShadow: `0 1px 3px 0 ${alpha(colors.grey[900], 0.1)}, 0 1px 2px 0 ${alpha(colors.grey[900], 0.06)}`,
          borderBottom: `1px solid ${colors.grey[200]}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#ffffff',
          borderRight: `1px solid ${colors.grey[200]}`,
          boxShadow: `4px 0 6px -1px ${alpha(colors.grey[900], 0.1)}, 2px 0 4px -1px ${alpha(colors.grey[900], 0.06)}`,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          borderRadius: 8,
          boxShadow: `0 1px 3px 0 ${alpha(colors.grey[900], 0.1)}, 0 1px 2px 0 ${alpha(colors.grey[900], 0.06)}`,
          border: `1px solid ${colors.grey[200]}`,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: `0 4px 6px -1px ${alpha(colors.grey[900], 0.1)}, 0 2px 4px -1px ${alpha(colors.grey[900], 0.06)}`,
            transform: 'translateY(-1px)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          textTransform: 'none' as const,
          fontWeight: 600,
          padding: '6px 12px',
          minHeight: 32,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: `0 4px 6px -1px ${alpha(colors.grey[900], 0.1)}, 0 2px 4px -1px ${alpha(colors.grey[900], 0.06)}`,
          },
        },
        contained: {
          boxShadow: `0 1px 3px 0 ${alpha(colors.grey[900], 0.1)}, 0 1px 2px 0 ${alpha(colors.grey[900], 0.06)}`,
          '&:hover': {
            boxShadow: `0 4px 6px -1px ${alpha(colors.grey[900], 0.1)}, 0 2px 4px -1px ${alpha(colors.grey[900], 0.06)}`,
          },
        },
        outlined: {
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: '#ffffff',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.primary[400],
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.primary[500],
              borderWidth: 2,
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
          '&.MuiChip-colorPrimary': {
            backgroundColor: alpha(colors.primary[500], 0.1),
            color: colors.primary[700],
            '&:hover': {
              backgroundColor: alpha(colors.primary[500], 0.2),
            },
          },
          '&.MuiChip-colorSecondary': {
            backgroundColor: alpha(colors.secondary[500], 0.1),
            color: colors.secondary[700],
            '&:hover': {
              backgroundColor: alpha(colors.secondary[500], 0.2),
            },
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '4px 8px',
          '&:hover': {
            backgroundColor: alpha(colors.primary[500], 0.08),
          },
          '&.Mui-selected': {
            backgroundColor: alpha(colors.primary[500], 0.12),
            color: colors.primary[700],
            '&:hover': {
              backgroundColor: alpha(colors.primary[500], 0.16),
            },
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: `1px solid ${colors.grey[200]}`,
          boxShadow: 'none',
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: '8px 0',
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&.Mui-expanded': {
            minHeight: 48,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: `0 20px 25px -5px ${alpha(colors.grey[900], 0.1)}, 0 10px 10px -5px ${alpha(colors.grey[900], 0.04)}`,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: colors.grey[200],
        },
        bar: {
          borderRadius: 4,
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: colors.primary[500],
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          '& .MuiAlert-root': {
            borderRadius: 8,
            fontWeight: 500,
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${colors.grey[200]}`,
        },
        indicator: {
          backgroundColor: colors.primary[500],
          height: 3,
          borderRadius: '2px 2px 0 0',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none' as const,
          fontWeight: 500,
          fontSize: '0.875rem',
          minHeight: 48,
          '&.Mui-selected': {
            color: colors.primary[600],
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${colors.grey[200]}`,
          boxShadow: `0 1px 3px 0 ${alpha(colors.grey[900], 0.1)}, 0 1px 2px 0 ${alpha(colors.grey[900], 0.06)}`,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: colors.grey[50],
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          color: colors.grey[700],
          borderBottom: `1px solid ${colors.grey[200]}`,
        },
        root: {
          borderBottom: `1px solid ${colors.grey[100]}`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
  },
  shadows: [
    'none',
    `0 1px 3px 0 ${alpha(colors.grey[900], 0.1)}, 0 1px 2px 0 ${alpha(colors.grey[900], 0.06)}`,
    `0 4px 6px -1px ${alpha(colors.grey[900], 0.1)}, 0 2px 4px -1px ${alpha(colors.grey[900], 0.06)}`,
    `0 10px 15px -3px ${alpha(colors.grey[900], 0.1)}, 0 4px 6px -2px ${alpha(colors.grey[900], 0.05)}`,
    `0 20px 25px -5px ${alpha(colors.grey[900], 0.1)}, 0 10px 10px -5px ${alpha(colors.grey[900], 0.04)}`,
    `0 25px 50px -12px ${alpha(colors.grey[900], 0.25)}`,
    'none', 'none', 'none', 'none', 'none', 'none', 'none', 'none', 'none', 'none',
    'none', 'none', 'none', 'none', 'none', 'none', 'none', 'none', 'none', 'none',
  ] as any,
});

export default theme;
